import { DEMO_CALCULATOR_STEP } from 'config/constants'
import { centerDemoField } from '../../center-field'
import {
  DEMO_ADDITIONAL_SERVICES_READING_DELAY,
  DEMO_STEP_DELAY,
  DEMO_STEP_PLAYBACK_COMPLETE_EVENT,
} from '../../playback-constants'
import {
  animateAction,
  animateDemoPlayerAction,
  scrollToDemoPlayerElement,
  wait,
  waitForDocumentElement,
  waitForDocumentElementToDisappear,
  waitForEnabledDocumentElement,
} from '../../helpers'

const chooseScenarioServices = async ({ findAction, controls }: DemoChooseServicesParams): Promise<boolean> => {
  await animateDemoPlayerAction({ action: findAction, isCancelled: controls.isCancelled })
  findAction.click()

  const saveModalAction = await waitForDocumentElement(
    '[data-demo-playback-action="save-additional-services-modal"]',
    controls.isCancelled,
  )

  if (!saveModalAction) {
    return false
  }

  const scenarioServiceIds = Array.from(
    document.querySelectorAll<HTMLElement>('[data-demo-playback-action="select-additional-service"]'),
  )
    .map(action => action.dataset.demoExtraServiceId)
    .filter((serviceId): serviceId is string => Boolean(serviceId))

  if (!scenarioServiceIds.length) {
    return false
  }

  for (const serviceId of scenarioServiceIds) {
    const selectServiceAction = document.querySelector<HTMLElement>(
      `[data-demo-playback-action="select-additional-service"]` +
      `[data-demo-extra-service-id="${serviceId}"]`,
    )

    if (!selectServiceAction) {
      return false
    }

    const isSelected = selectServiceAction.closest('[data-demo-extra-service-selected="true"]')

    if (isSelected) {
      continue
    }

    await animateDemoPlayerAction({ action: selectServiceAction, isCancelled: controls.isCancelled })
    selectServiceAction.click()

    const selectedService = await waitForDocumentElement(
      '[data-demo-scenario-extra-service="true"]' +
      '[data-demo-extra-service-selected="true"] ' +
      `[data-demo-extra-service-id="${serviceId}"]`,
      controls.isCancelled,
    )

    if (!selectedService) {
      return false
    }
  }

  const enabledSaveModalAction = await waitForEnabledDocumentElement(
    '[data-demo-playback-action="save-additional-services-modal"]',
    controls.isCancelled,
  )

  if (!enabledSaveModalAction) {
    return false
  }

  await animateAction(enabledSaveModalAction, false)
  enabledSaveModalAction.click()

  const modalIsClosed = await waitForDocumentElementToDisappear(
    '[data-demo-playback-action="save-additional-services-modal"]',
    controls.isCancelled,
  )

  if (!modalIsClosed) {
    return false
  }

  const selectedAdditionalService = await waitForDocumentElement(
    '[data-calculator-step="additional_services"] .additional-company-card_active ' +
    '[data-demo-scenario-extra-service="true"]' +
    '[data-demo-extra-service-selected="true"]',
    controls.isCancelled,
  )

  if (!selectedAdditionalService) {
    return false
  }

  const scrolledInPlayer = await scrollToDemoPlayerElement({ element: selectedAdditionalService, isCancelled: controls.isCancelled })

  if (controls.isCancelled()) {
    return false
  }

  if (!scrolledInPlayer &&
    !centerDemoField(selectedAdditionalService)) {

    selectedAdditionalService.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }
  await wait(DEMO_ADDITIONAL_SERVICES_READING_DELAY)

  return true
}

export const playAdditionalServicesStep = async ({
  controls,
  fields,
  onComplete,
  stepElement,
}: AdditionalServicesStepParams): Promise<boolean> => {
  const findAction = stepElement.querySelector<HTMLElement>(
    '[data-demo-playback-action="find-additional-services"]',
  )
  const skipAction = stepElement.querySelector<HTMLElement>(
    '[data-demo-playback-action="skip-additional-services"]',
  )

  if (findAction) {
    const servicesAreSelected = await chooseScenarioServices({ findAction, controls })

    if (!servicesAreSelected) {
      return false
    }
  } else if (skipAction) {
    await animateDemoPlayerAction({ action: skipAction, isCancelled: controls.isCancelled })
    skipAction.click()
    await wait(DEMO_STEP_DELAY)
  } else {
    return false
  }

  const saveAdditionalServicesAction = await waitForEnabledDocumentElement(
    '[data-calculator-step="additional_services"] ' +
    '[data-demo-playback-action="save-additional-services"]',
    controls.isCancelled,
  )

  if (!saveAdditionalServicesAction) {
    return false
  }

  await animateDemoPlayerAction({ action: saveAdditionalServicesAction, isCancelled: controls.isCancelled })
  saveAdditionalServicesAction.click()

  const additionalServicesAreSaved = await waitForDocumentElement(
    '[data-demo-playback-action="save-additional-services"]' +
    '[data-demo-playback-complete="true"]',
    controls.isCancelled,
  )

  if (!additionalServicesAreSaved) {
    return false
  }

  controls.dispatchEvent(new CustomEvent(DEMO_STEP_PLAYBACK_COMPLETE_EVENT, {
    detail: { step: DEMO_CALCULATOR_STEP.ADDITIONAL_SERVICES },
  }))
  onComplete()
  controls.clearFields(fields)

  return true
}
